/**
 * #651 — orphaned content script guard.
 *
 * When Chrome updates or reloads the extension while a WhatsApp tab stays open, the old content
 * script keeps running: its UI still renders, but every chrome.* call throws "Extension context
 * invalidated", so clicks silently do nothing (Sarah 0120985b). `onInstalled('update')` reloads
 * WhatsApp tabs, but not a tab that was discarded/frozen at the time, a manual reload from
 * chrome://extensions, or a reload that raced. The update modal can't help either: background
 * messages drive it, and an orphaned script never receives them.
 *
 * The guard checks every ORPHAN_CHECK_INTERVAL_MS, when the tab becomes visible, and on every
 * click (capture phase, before the target handler). On the first alive→dead transition it calls
 * `onOrphaned` once and stops. It only arms if the context is alive at boot: the web app has no
 * chrome.runtime at all, so "dead" alone is not "orphaned"; "was alive, now dead" is.
 */

export const ORPHAN_CHECK_INTERVAL_MS = 15_000;

export type OrphanCheckStep = 'interval' | 'visibility' | 'click';

/** The slice of `chrome.runtime` the check reads. */
export interface RuntimeProbe {
  id?: string;
  getManifest?: () => unknown;
}

/** The slice of `document` the guard listens on (injectable for tests). */
export interface OrphanGuardDoc {
  readonly visibilityState: DocumentVisibilityState;
  addEventListener(type: string, listener: () => void, options?: boolean): void;
  removeEventListener(type: string, listener: () => void, options?: boolean): void;
}

export interface OrphanGuard {
  /** Run a check now. Returns false once the context is known dead. Never throws. */
  check(step: OrphanCheckStep): boolean;
  cancel(): void;
}

export interface OrphanGuardOptions {
  readRuntime: () => RuntimeProbe | undefined;
  onOrphaned: (step: OrphanCheckStep) => void;
  doc: OrphanGuardDoc;
  intervalMs?: number;
}

/** Reading `chrome.runtime` itself can throw in an orphaned context, so the read is injected. */
export function isExtensionContextAlive(readRuntime: () => RuntimeProbe | undefined): boolean {
  try {
    const runtime = readRuntime();
    if (!runtime?.id || typeof runtime.getManifest !== 'function') return false;
    runtime.getManifest();
    return true;
  } catch {
    return false;
  }
}

/** The live `chrome.runtime`, or undefined outside an extension. */
export function readChromeRuntime(): RuntimeProbe | undefined {
  return typeof chrome !== 'undefined' ? chrome.runtime : undefined;
}

export function armOrphanGuard(options: OrphanGuardOptions): OrphanGuard {
  const { readRuntime, onOrphaned, doc, intervalMs = ORPHAN_CHECK_INTERVAL_MS } = options;
  if (!isExtensionContextAlive(readRuntime)) {
    return { check: () => true, cancel: () => undefined };
  }

  let orphaned = false;
  let stopped = false;
  let timer: ReturnType<typeof setInterval> | null = null;

  const stop = () => {
    stopped = true;
    if (timer !== null) clearInterval(timer);
    timer = null;
    doc.removeEventListener('visibilitychange', onVisibility);
    doc.removeEventListener('click', onClick, true);
  };

  const check = (step: OrphanCheckStep): boolean => {
    if (orphaned) return false;
    if (stopped || isExtensionContextAlive(readRuntime)) return true;
    orphaned = true;
    stop();
    try {
      onOrphaned(step);
    } catch (e) {
      console.debug('[orphan-guard] onOrphaned failed', e);
    }
    return false;
  };

  function onVisibility() {
    if (doc.visibilityState === 'visible') check('visibility');
  }
  function onClick() {
    check('click');
  }

  timer = setInterval(() => check('interval'), intervalMs);
  doc.addEventListener('visibilitychange', onVisibility);
  doc.addEventListener('click', onClick, true);

  return { check, cancel: stop };
}

let activeGuard: OrphanGuard | null = null;

/** Register the tab's guard (cancelling any previous one). `null` clears it. */
export function setActiveOrphanGuard(guard: OrphanGuard | null): void {
  if (activeGuard && activeGuard !== guard) activeGuard.cancel();
  activeGuard = guard;
}

/** Call first in a click handler: false = orphaned (the guard has shown the banner), so bail. */
export function isContentScriptAlive(): boolean {
  return activeGuard ? activeGuard.check('click') : true;
}
